import { useTranslation } from 'react-i18next';
import { useUpdateEffect } from 'react-use';

import { CompanyReadModelSort, SortOrder } from '@client/api';
import { DropDownSort, DropDownSortEntry, SearchField, useSearching, useSorting } from '@client/toolkit';

import { useCompanyListSearchQueryParams, useCompanyListSortQueryParams } from '../utils/use-company-list-query-params';

const SORT_OPTIONS: DropDownSortEntry<CompanyReadModelSort, SortOrder>[] = [
  { property: 'Name', order: 'Asc', label: 'legalPerson.name' },
  { property: 'Name', order: 'Desc', label: 'legalPerson.name' },
  { property: 'VatId', order: 'Asc', label: 'legalPerson.vatId' },
  { property: 'VatId', order: 'Desc', label: 'legalPerson.vatId' },
];

interface CompaniesToolbarProps {
  totalCount?: number;
}

export const CompaniesToolbar = ({ totalCount }: CompaniesToolbarProps) => {
  const { t: tCommon } = useTranslation();
  const { t: tGlobal } = useTranslation('global');

  const [searchQueryParams, setSearchQueryParam] = useCompanyListSearchQueryParams();
  const [sortQueryParams, setSortQueryParam] = useCompanyListSortQueryParams();
  const { search, clearSearch, debouncedSearch, updateSearch } = useSearching({
    search: searchQueryParams.companiesSearch,
  });
  const { sortProperty, sortOrder, updateSortOrder, updateSortProperty } = useSorting<CompanyReadModelSort, SortOrder>({
    sortProperty: sortQueryParams.companiesSortProperty ?? 'Name',
    sortOrder: sortQueryParams.companiesSortOrder ?? 'Asc',
  });

  useUpdateEffect(() => {
    setSearchQueryParam({
      companiesSearch: debouncedSearch ?? undefined,
    });
  }, [debouncedSearch]);

  useUpdateEffect(() => {
    setSortQueryParam({
      companiesSortOrder: sortOrder ?? undefined,
    });
  }, [sortOrder]);

  useUpdateEffect(() => {
    setSortQueryParam({
      companiesSortProperty: sortProperty ?? undefined,
    });
  }, [sortProperty]);

  return (
    <div className="flex justify-between items-center py-4">
      <div className="flex w-6/12 items-center">
        <SearchField value={search} onChange={updateSearch} onClear={clearSearch} placeholder={tCommon('search')} />
        <div className="flex">
          <span className="px-2 whitespace-nowrap text-white">{`${totalCount} ${tCommon('entry', {
            count: totalCount ?? 0,
            context: totalCount && totalCount > 1 ? 'plural' : 'singular',
          })}`}</span>
        </div>
      </div>
      <DropDownSort<CompanyReadModelSort, SortOrder>
        selected={{
          order: sortOrder,
          property: sortProperty,
        }}
        onSortChange={(entry) => {
          updateSortOrder(entry.order);
          updateSortProperty(entry.property);
        }}
        promptLabel={`${tCommon('sortBy')}:`}
        getSelectedLabel={(label) => (label ? tGlobal(label) : '')}
        getOptionLabel={(label) => (label ? tGlobal(label) : '')}
        ascLabel={tCommon('asc')}
        descLabel={tCommon('desc')}
        columns={SORT_OPTIONS}
      />
    </div>
  );
};
